import React, { useState } from 'react';
import { Typography, LoadingButton, Checkbox } from '@one-thd/sui-atomic-components';
import { SweepstakesMarkdown } from '../../../utils/SweepstakesMarkdown';
import { windowExists } from '../../../../../common/utils/AccountUtils';
import { useFetchSweepstakesAccept } from '../../../../../hooks';
import { useSweepstakesData } from '../../../../../contexts/SweepstakesDataContext';
import { RenderGenericError } from '../../SweepstakesUtils';

// Render components
const RenderCheckbox = ({ checked, onChange, text, testId }) => (
  <div className="sui-flex sui-items-center">
    <div className="sui-mr-2 sui-mt-1">
      <Checkbox
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        data-testid={testId}
      />
    </div>
    <SweepstakesMarkdown markdownText={text} disableSpacing />
  </div>
);

const RenderFooterContent = ({
  acceptError,
  isLoadingAccept,
  isButtonDisabled,
  handleTakeSurvey,
  ctaTitle,
}) => (
  <div className="sui-space-y-2">
    {acceptError && (
      <div className="sui-py-3">
        <RenderGenericError
          refresh={handleTakeSurvey}
          testId="sweepstakes-accept-error"
        />
      </div>
    )}
    <LoadingButton
      variant="primary"
      fullWidth
      disabled={isButtonDisabled}
      loading={isLoadingAccept}
      onClick={handleTakeSurvey}
      data-testid="opt-in-take-survey-button"
    >
      {ctaTitle}
    </LoadingButton>
  </div>
);

export const OptInContent = () => {
  const { sweepstakesData, forceAcceptSuccess } = useSweepstakesData();
  const {
    preOptInDrawerDescription,
    postOptInDrawerDescription,
    preOptInDrawerCta,
    optInCheckboxes = [],
  } = sweepstakesData || {};

  // Track checked state of each checkbox by index
  const [checkedStates, setCheckedStates] = useState({});
  const { isLoadingAccept, acceptError, acceptSweepstakes } = useFetchSweepstakesAccept({ useRestAPI: true });

  const areAllChecked = optInCheckboxes?.length > 0
    && optInCheckboxes.every((item, index) => checkedStates[index]);

  const handleCheckboxChange = (index, checked) => {
    setCheckedStates((prev) => ({
      ...prev,
      [index]: checked,
    }));
  };

  const handleTakeSurvey = async () => {
    if (areAllChecked) {
      let result;

      if (forceAcceptSuccess) {
        // eslint-disable-next-line no-console
        console.log('OptInContent -- Bypassing acceptSweepstakes call! (for testing purposes)');
        result = { success: true };
      } else {
        const { challengeId, customerChallengeId, isV2 } = sweepstakesData || {};
        result = await acceptSweepstakes({ challengeId, customerChallengeId, isV2 });
      }

      if (result.success && windowExists()) {
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.sweepstakes-accepted', {
          termsAccepted: true,
          associateConsent: true,
        });
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.open-sweepstakes-survey-drawer', {});
      }
    }
  };

  const isButtonDisabled = !areAllChecked || acceptError;
  const description = postOptInDrawerDescription || preOptInDrawerDescription;

  const content = (
    <div className="sui-space-y-4" data-testid="sweepstakes-opt-in-content">
      <div className="sui-space-y-4">
        {description && <SweepstakesMarkdown markdownText={description} />}
      </div>
      <div className="sui-space-y-2 sui-pt-4">
        {optInCheckboxes.map((text, index) => (
          <RenderCheckbox
            key={`opt-in-checkbox-${index}`}
            checked={!!checkedStates[index]}
            onChange={(checked) => handleCheckboxChange(index, checked)}
            text={text}
            testId={`opt-in-checkbox-${index}`}
          />
        ))}
      </div>
    </div>
  );

  const footer = (
    <RenderFooterContent
      acceptError={acceptError}
      isLoadingAccept={isLoadingAccept}
      isButtonDisabled={isButtonDisabled}
      handleTakeSurvey={handleTakeSurvey}
      ctaTitle={preOptInDrawerCta || 'Take Survey'}
    />
  );

  return { content, footer };
};